/**
 * Dashboard AppSidebar — Chatbase-style org-level sidebar.
 *
 * Simplified navigation:
 *   - Agents (primary)
 *   - Live Chat (collapsible: Conversations, Human Agents, Teams)
 *   - Analytics
 *   - Integrations
 *   - Plans & Billing, Settings
 *   - Developers (collapsible: API Docs, API Guide, API Keys)
 *   - User footer with dropdown
 */

import React from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import {
  Bot,
  MessageCircle,
  UserCheck,
  UsersIcon,
  BarChart3,
  Settings,
  LogOut,
  CreditCard,
  Key as KeyIcon,
  Link,
  Code,
  ChevronsUpDown,
  ChevronRight,
  Activity,
  Sparkles,
} from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { logger } from '../../utils/logger'
import { useUnreadCounts } from '../../hooks/useRealTimeNotifications'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from '../ui/sidebar'
import { Avatar, AvatarFallback } from '../ui/Avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/ShadcnDropdownMenu'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible'

interface NavigationItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  external?: boolean
}

/** Check if a nav item is active based on the current path */
function isItemActive(href: string, pathname: string): boolean {
  if (href === '/dashboard') return pathname === '/dashboard'
  // /dashboard/agents/new is the create-agent wizard (belongs to Chatbots, not Human Agents)
  if (href === '/dashboard/agents' && pathname.startsWith('/dashboard/agents/new')) return false
  return pathname.startsWith(href)
}

export function AppSidebar() {
  const { logout, user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { totalUnread } = useUnreadCounts()

  const handleLogout = async () => {
    logger.debug('Logout initiated from sidebar', {
      component: 'Sidebar',
      action: 'logout_click',
      userId: user?.id,
    })
    try {
      await logout()
    } catch (error) {
      logger.error('Logout error', { error })
    } finally {
      window.location.href = '/auth/login'
    }
  }

  const userInitial =
    user?.full_name?.charAt(0)?.toUpperCase() ||
    user?.email?.charAt(0)?.toUpperCase() ||
    'U'

  // Live Chat sub-items
  const liveChatItems: NavigationItem[] = [
    { name: 'Conversations', href: '/dashboard/conversations', icon: MessageCircle },
    { name: 'Human Agents', href: '/dashboard/agents', icon: UserCheck },
    { name: 'Teams', href: '/dashboard/teams', icon: UsersIcon },
  ]
  const liveChatActive = liveChatItems.some(item => isItemActive(item.href, location.pathname))

  // Developer sub-items
  const developerItems: NavigationItem[] = [
    { name: 'API Docs', href: 'https://agenticai-2a3d1aac.mintlify.app/introduction', icon: Code, external: true },
    { name: 'API Keys', href: '/dashboard/developers/api-keys', icon: KeyIcon },
    { name: 'API Logs', href: '/dashboard/developers/api-logs', icon: Activity },
  ]
  const devActive = developerItems.some(item => !item.external && isItemActive(item.href, location.pathname))

  return (
    <Sidebar collapsible="icon">
      {/* Logo */}
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild tooltip="CleverAct AI">
              <NavLink to="/dashboard">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <Bot className="size-4" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">CleverAct AI</span>
                  <span className="truncate text-xs text-sidebar-foreground/60">AI Platform</span>
                </div>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* Navigation */}
      <SidebarContent>
        {/* Primary: Agents */}
        <SidebarGroup>
          <SidebarGroupLabel>Chatbots</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isItemActive('/dashboard/chatbots', location.pathname) || location.pathname.startsWith('/dashboard/agents/new')}
                  tooltip="Agents"
                >
                  <NavLink to="/dashboard/chatbots">
                    <Bot />
                    <span>Agents</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Live Chat (collapsible) */}
        <SidebarGroup>
          <SidebarGroupLabel>Live Chat</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <Collapsible defaultOpen={liveChatActive} className="group/collapsible">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip="Live Chat" isActive={liveChatActive}>
                      <MessageCircle />
                      <span>Live Chat</span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  {totalUnread > 0 && (
                    <SidebarMenuBadge>{totalUnread}</SidebarMenuBadge>
                  )}
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {liveChatItems.map((item) => (
                        <SidebarMenuSubItem key={item.name}>
                          <SidebarMenuSubButton
                            asChild
                            isActive={isItemActive(item.href, location.pathname)}
                          >
                            <NavLink to={item.href}>
                              <span>{item.name}</span>
                            </NavLink>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Analytics + Integrations */}
        <SidebarGroup>
          <SidebarGroupLabel>Insights</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isItemActive('/dashboard/analytics', location.pathname)}
                  tooltip="Analytics"
                >
                  <NavLink to="/dashboard/analytics">
                    <BarChart3 />
                    <span>Analytics</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isItemActive('/dashboard/integrations', location.pathname)}
                  tooltip="Integrations"
                >
                  <NavLink to="/dashboard/integrations">
                    <Link />
                    <span>Integrations</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Account */}
        <SidebarGroup>
          <SidebarGroupLabel>Account</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isItemActive('/dashboard/plans', location.pathname)}
                  tooltip="Plans & Billing"
                >
                  <NavLink to="/dashboard/plans">
                    <CreditCard />
                    <span>Plans & Billing</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isItemActive('/dashboard/settings', location.pathname)}
                  tooltip="Settings"
                >
                  <NavLink to="/dashboard/settings">
                    <Settings />
                    <span>Settings</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Developers (collapsible) */}
        <SidebarGroup>
          <SidebarGroupLabel>Developers</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <Collapsible defaultOpen={devActive} className="group/collapsible">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip="Developers" isActive={devActive}>
                      <Code />
                      <span>Developers</span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {developerItems.map((item) => (
                        <SidebarMenuSubItem key={item.name}>
                          <SidebarMenuSubButton
                            asChild
                            isActive={!item.external && isItemActive(item.href, location.pathname)}
                          >
                            {item.external ? (
                              <a href={item.href} target="_blank" rel="noopener noreferrer">
                                <span>{item.name}</span>
                              </a>
                            ) : (
                              <NavLink to={item.href}>
                                <span>{item.name}</span>
                              </NavLink>
                            )}
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Resources */}
        <SidebarGroup>
          <SidebarGroupLabel>Resources</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isItemActive('/dashboard/changelog', location.pathname)}
                  tooltip="What's New"
                >
                  <NavLink to="/dashboard/changelog">
                    <Sparkles />
                    <span>What's New</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* User profile footer */}
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  tooltip={user?.full_name || 'User'}
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                      {userInitial}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {user?.full_name || 'User'}
                    </span>
                    <span className="truncate text-xs">{user?.email}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="bottom"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg">
                        {userInitial}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {user?.full_name || 'User'}
                      </span>
                      <span className="truncate text-xs">{user?.email}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                  <Settings className="mr-2 size-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/plans')}>
                  <CreditCard className="mr-2 size-4" />
                  Plans & Billing
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 size-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}

export default AppSidebar
